import { Box, Tab, Tabs } from "@mui/material";
import { useIntl } from "react-intl";
import { Button, Col } from "reactstrap";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import { useMobile } from "../Layout/UseResponsive";
import PartNewRequestComponent from "./PartNewRequestComponent";
import { PartsRequestedTableComponent } from "./PartsRequestedTableComponent";
import { PartsUsedTableComponent } from "./PartsUsedTableComponent";

type AssistanceNavBarType = {
    mode: "workshop" | "backoffice";
    childrenColumnSize?: number;
    assistanceId: number;
    reload: boolean;
    setActiveTab: React.Dispatch<React.SetStateAction<number>>;
    activeTab: number;
    isComplete: boolean;
    isAcceptedBudget: boolean;
    assistanceStatus: string;
}

export function AssistancePartsNavBar({ mode, childrenColumnSize = 12, assistanceId, reload, setActiveTab, activeTab, isComplete, isAcceptedBudget, assistanceStatus }: AssistanceNavBarType) {
    const intl = useIntl();
    const isMobile = useMobile();
    const { availableModules } = useAvailableModulesContext();
    const isActiveWarehouseModule = availableModules.includes("IsActiveWarehouseModule")

    // Calculamos o offset da navbar porque queremos esconder opções
    // quando estamos em vistas diferentes 
    const navbarIndexOffset = mode === "backoffice" ? -1 : 0;

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const tabs = [
        {
            id: "Screens.Assistances.Parts.Navbar.PartsUsed",
            alwaysVisible: true
        },
        {
            id: "Screens.Assistances.Parts.Navbar.PartsRequested",
            requiresWarehouseModule: true
        },
        {
            id: "Screens.Assistances.Parts.Navbar.PartsNewRequest",
            requiresWarehouseModule: true
        }
    ];

    const visibleTabs = tabs.filter(tab => tab.alwaysVisible || (tab.requiresWarehouseModule && isActiveWarehouseModule));

    return (
        <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {isMobile ? (
                    <div style={{
                        display: "flex",
                        overflowX: "auto",
                        gap: "8px",
                        padding: "5px 5px 10px 5px",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none"
                    }}>
                        <style>{`
                            div::-webkit-scrollbar {
                                display: none;
                            }
                        `}</style>
                        {visibleTabs.map((tab, index) => (
                            <Button
                                key={tab.id}
                                onClick={() => setActiveTab(1 + navbarIndexOffset + index)}
                                style={{
                                    backgroundColor: activeTab === (1 + navbarIndexOffset + index) ? "var(--button-main-color)" : "white",
                                    color: activeTab === (1 + navbarIndexOffset + index) ? "white" : "#6c757d",
                                    border: activeTab === (1 + navbarIndexOffset + index) ? "1px solid var(--button-main-color)" : "1px solid #dee2e6",
                                    borderRadius: "20px",
                                    padding: "8px 16px",
                                    whiteSpace: "nowrap",
                                    fontSize: "14px",
                                    fontWeight: activeTab === (1 + navbarIndexOffset + index) ? "500" : "400",
                                    minWidth: "auto",
                                    transition: "all 0.2s ease"
                                }}
                            >
                                {intl.formatMessage({ id: tab.id })}
                            </Button>
                        ))}
                    </div>
                ) : (
                    <Tabs
                        value={activeTab}
                        onChange={handleChange}
                        aria-label="basic tabs example"
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                    >
                        {visibleTabs.map((tab, index) => (
                            <Tab
                                key={tab.id}
                                label={intl.formatMessage({ id: tab.id })}
                                {...a11yProps(navbarIndexOffset + index)}
                            />
                        ))}
                    </Tabs>
                )}
            </Box>
            <TabPanel
                value={activeTab}
                index={1 + navbarIndexOffset}
                childrenColumnSize={childrenColumnSize}
            >
                <PartsUsedTableComponent
                    mode={mode}
                    assistanceId={assistanceId}
                    reload={reload}
                    isComplete={isComplete}
                    isAcceptedBudget={isAcceptedBudget}
                    assistanceStatus={assistanceStatus}
                />
            </TabPanel>
            {isActiveWarehouseModule && (
                <>
                    <TabPanel
                        value={activeTab}
                        index={2 + navbarIndexOffset}
                        childrenColumnSize={childrenColumnSize}
                    >
                        <PartsRequestedTableComponent
                            mode={mode}
                            assistanceId={assistanceId}
                            setActiveTab={setActiveTab}
                            reload={reload}
                            isComplete={isComplete}
                            isAcceptedBudget={isAcceptedBudget}
                        />
                    </TabPanel>
                    <TabPanel
                        value={activeTab}
                        index={3 + navbarIndexOffset}
                        childrenColumnSize={childrenColumnSize}
                    >
                        <PartNewRequestComponent
                            mode={mode}
                            assistanceId={assistanceId}
                            setActiveTab={setActiveTab}
                            reload={reload}
                            isComplete={isComplete}
                            isAcceptedBudget={isAcceptedBudget}
                        />
                    </TabPanel>
                </>
            )}
        </>
    )
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    childrenColumnSize: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, childrenColumnSize, ...other } = props;

    return (
        <Col xl={childrenColumnSize}>
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <>{children}</>
                )}
            </div>
        </Col>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}